import {
  useMarkPageCompleted,
  useOnboardingProgress,
  usePlatformConfig,
} from "@aurem/components";
import {
  nationalitiesWithISO as citizenshipsWithISO,
  countriesWithFlag,
} from "@aurem/nationalities";
import { Toast, type SelectOption } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAfter, subYears } from "date-fns";
import { CitizenshipConfirmationModal } from "layouts/employee/citizenship-confirmation/components/citizenship-confirmation-modal";
import { useEmployeeDetails } from "layouts/employee/citizenship-confirmation/hooks/use-employee-details";
import { useUpdateEmployeeCitizenship } from "layouts/employee/citizenship-confirmation/hooks/use-update-employee-citizenship";
import { useEmployment } from "providers/employee/EmploymentProvider";
import { useI18n } from "providers/I18nProvider";
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { ProgressPageName } from "types";
import { z } from "zod";

export interface CitizenshipConfirmationContextType {
  confirmCitizenship: (citizenshipCode: string) => void;
  isCountryBlocked: (citizenshipCode: string) => boolean;
  citizenshipOptions: SelectOption[];
  isCitizenshipUpToDate: () => boolean;
}

export const CitizenshipConfirmationContext = createContext<
  CitizenshipConfirmationContextType | undefined
>(undefined);

interface CitizenshipConfirmationProviderProps {
  children: ReactNode;
}

type CitizenshipFormValues = {
  citizenships: SelectOption[];
  citizenshipConfirmation: boolean;
};

const citizenshipFormSchema = z.object({
  citizenships: z.array(z.custom<SelectOption>()),
  citizenshipConfirmation: z.boolean(),
});

export const CitizenshipConfirmationProvider = ({
  children,
}: CitizenshipConfirmationProviderProps) => {
  const { t } = useI18n();
  const { onboardingProgress, loading: onboardingLoading } =
    useOnboardingProgress();
  const platformConfig = usePlatformConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { employment, loading: employmentLoading } = useEmployment();
  const { employee, refetch: refetchEmployee } = useEmployeeDetails();
  const { updateCitizenship } = useUpdateEmployeeCitizenship();
  const { markPageCompleted } = useMarkPageCompleted();

  const blockedCitizenships = platformConfig.blockedCitizenships || [];

  const currentCitizenshipCode = employee?.nationality
    ? citizenshipsWithISO[employee.nationality]
    : undefined;

  const isCountryBlocked = useCallback(
    (countryCode: string): boolean => {
      return blockedCitizenships.includes(countryCode);
    },
    [blockedCitizenships],
  );

  const isCitizenshipUpToDate = useCallback((): boolean => {
    const anyBlockedCitizenships =
      (platformConfig.blockedCitizenships || []).length > 0;
    if (!anyBlockedCitizenships) return true;

    const pageProgressItem = onboardingProgress?.find(
      (item) => item.page === ProgressPageName.CitizenshipConfirmation,
    );

    if (pageProgressItem?.updatedAt) {
      const lastUpdate = new Date(pageProgressItem.updatedAt);
      const oneYearAgo = subYears(new Date(), 1);

      return isAfter(lastUpdate, oneYearAgo);
    }

    return false;
  }, [onboardingProgress, platformConfig.blockedCitizenships]);

  const citizenshipOptions: SelectOption[] = useMemo(() => {
    const flagByCode = new Map(countriesWithFlag.map((c) => [c.code, c.flag]));

    return Object.entries(citizenshipsWithISO)
      .map(([name, code]) => ({
        id: code,
        text: name,
        icon: flagByCode.get(code),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, []);

  const blockedCitizenshipNames = useMemo(() => {
    return Object.entries(citizenshipsWithISO)
      .filter(([_, code]) => blockedCitizenships.includes(code))
      .map(([name]) => name)
      .join(", ");
  }, [blockedCitizenships]);

  const defaultCitizenshipOption = currentCitizenshipCode
    ? citizenshipOptions.find((opt) => opt.id === currentCitizenshipCode)
    : undefined;

  const form = useForm<CitizenshipFormValues>({
    resolver: zodResolver(citizenshipFormSchema),
    defaultValues: {
      citizenships: defaultCitizenshipOption ? [defaultCitizenshipOption] : [],
      citizenshipConfirmation: false,
    },
  });

  const confirmCitizenship = useCallback(
    async (citizenshipCode: string): Promise<void> => {
      const citizenshipName = Object.entries(citizenshipsWithISO).find(
        ([_, code]) => code === citizenshipCode,
      )?.[0];

      if (!citizenshipName) return;

      try {
        await updateCitizenship([citizenshipName]);
        void refetchEmployee();
        await markPageCompleted(ProgressPageName.CitizenshipConfirmation);
      } catch (error) {
        Toast.error({
          title: t("apps.employee.common.unexpectedError.title"),
          text: t("apps.employee.common.unexpectedError.description"),
        });
      }
    },
    [updateCitizenship, refetchEmployee, markPageCompleted, t],
  );

  const handleConfirm = useCallback(async () => {
    const values = form.getValues();
    if (values.citizenships?.length && values.citizenshipConfirmation) {
      const citizenshipNames = values.citizenships.map((opt) => opt.text);

      if (citizenshipNames.length > 0) {
        try {
          await updateCitizenship(citizenshipNames);
          void refetchEmployee();
          await markPageCompleted(ProgressPageName.CitizenshipConfirmation);
          Toast.success({
            title: t(
              "apps.employee.pages.citizenshipConfirmation.toast.success.title",
            ),
            text: t(
              "apps.employee.pages.citizenshipConfirmation.toast.success.text",
            ),
          });
          setIsModalOpen(false);
        } catch (error) {
          Toast.error({
            title: t("apps.employee.common.unexpectedError.title"),
            text: t("apps.employee.common.unexpectedError.description"),
          });
        }
      }
    }
  }, [form, updateCitizenship, refetchEmployee, markPageCompleted, t]);

  useEffect(() => {
    if (onboardingLoading || employmentLoading) return;

    const today = new Date();
    const hasEndedBenefit = employment?.productAssignments?.some(
      (productAssignment) => {
        if (!productAssignment.endDate) return false;
        const endDate = new Date(productAssignment.endDate);
        return today >= endDate;
      },
    );

    if (hasEndedBenefit) {
      setIsModalOpen(false);
      return;
    }

    const shouldShowModal =
      !isCitizenshipUpToDate() && blockedCitizenships.length > 0;
    setIsModalOpen(shouldShowModal);
  }, [
    isCitizenshipUpToDate,
    blockedCitizenships,
    employment,
    onboardingLoading,
    employmentLoading,
  ]);

  const contextValue: CitizenshipConfirmationContextType = {
    confirmCitizenship,
    isCountryBlocked,
    citizenshipOptions,
    isCitizenshipUpToDate,
  };

  return (
    <CitizenshipConfirmationContext.Provider value={contextValue}>
      {children}
      <CitizenshipConfirmationModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        citizenshipOptions={citizenshipOptions}
        blockedCitizenshipNames={blockedCitizenshipNames}
        onConfirm={handleConfirm}
        form={form}
      />
    </CitizenshipConfirmationContext.Provider>
  );
};
